import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useCanAccess } from '@permissions/useFeatures';
import { useMonitorReplay, useReplayState, type IReplayState } from '../hooks';
import type { IReplayView } from './buildReplayView';
import type { IReplayBarProps } from './ReplayBar';
import { crossedBetween, frameAt, indexReplay, safetyReached } from './replayIndex';
import type { ReplaySafetyEvent } from './replayRail';
import { useActiveEvents } from './useActiveEvents';
import { useHeldDay } from './useHeldDay';
import type { ReplaySpeed } from './usePlayback';
import { useReplayView } from './useReplayView';

/** The playhead in the site zone, for the card title and the page subtitle. */
export interface IReplayStamp {
  date: string;
  time: string;
}

export interface IReplayController {
  /** The bar is open (live or replaying). */
  open: boolean;
  toggle(): void;
  /** The building and Live Summary at the playhead; null while live. */
  view: IReplayView | null;
  /** The bar's props; null while the bar is closed. */
  barProps: IReplayBarProps | null;
  /** The playhead in the site zone; null while live. */
  stamp: IReplayStamp | null;
  /** The day's replay failed to load. */
  failed: boolean;
  /** A replayed day is loading: `view` is the previous frame (or an empty one), to be shown dimmed. */
  loading: boolean;
}

const MINUTE_MS = 60_000;
const NO_SAFETY: readonly ReplaySafetyEvent[] = [];

/** The stamp for `at`, memoized per site minute so the ticks inside one minute keep the object. */
function useStamp(at: number | null, tz: string): IReplayStamp | null {
  const { i18n } = useTranslation();
  const minute = at === null ? null : Math.floor(at / MINUTE_MS);
  return useMemo(() => {
    if (minute === null) return null;
    const site = DateTime.fromMillis(minute * MINUTE_MS, { zone: tz }).setLocale(i18n.language);
    return { date: site.toLocaleString({ weekday: 'short', month: 'short', day: 'numeric' }), time: site.toFormat('HH:mm') };
  }, [minute, tz, i18n.language]);
}

/**
 * Composes the Monitor replay: URL state, the day's data, the playhead, the
 * crossed events (`useActiveEvents`) and the view (`useReplayView`). ReplayBar
 * owns the playback clock and reports every move through `onAt`; the exact
 * value is handed back as `atMs`. A move counts as playback — and shows the
 * events it crossed forward — only while playing and when no commit follows it
 * in the same handler (a marker click is a jump).
 */
export function useReplayController(siteId: string, siteName: string, tz: string): IReplayController {
  const state = useReplayState(tz);
  // History is gated: without analytics.view even a shared `?replay=` link stays live.
  const canReplay = useCanAccess('analytics.view');
  const open = canReplay && state.open;
  const today = DateTime.now().setZone(tz).toFormat('yyyy-LL-dd');
  const query = useMonitorReplay(siteId, open ? (state.date ?? today) : null, today);
  const index = useMemo(() => query.data && indexReplay(query.data), [query.data]);

  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<ReplaySpeed>(30);
  const playingRef = useRef(false);
  /** The playhead set through `onAt`; cleared whenever the day or mode changes. */
  const [local, setLocal] = useState<number | null>(null);
  const at = canReplay ? (local ?? state.atMs) : null;
  const atRef = useRef<number | null>(null);
  const events = useActiveEvents(local);
  const { queue, clear } = events;

  useEffect(() => {
    if (at !== null) atRef.current = at;
  }, [at]);

  const frame = at !== null && index ? frameAt(index, at) : null;
  // Keyed on the count, so the rail's alerts rebuild only when the playhead crosses one.
  const reached = at !== null && index ? safetyReached(index, at) : 0;
  const safety = useMemo(() => index?.safety.slice(0, reached) ?? NO_SAFETY, [index, reached]);
  const view = useReplayView(frame, at !== null, query.data?.equipment, events.active, safety);
  const stamp = useStamp(at, tz);

  const pending = open && query.isPending;
  const loading = pending && at !== null;
  const { held, hold } = useHeldDay(query.data, at, view, open && !query.isError);

  const onPlayingChange = useCallback((next: boolean) => {
    playingRef.current = next;
    setPlaying(next);
  }, []);
  const { close: closeState, selectDate: selectStateDate, commitAt: commitStateAt, openBar } = state;
  const close = useCallback(() => {
    onPlayingChange(false);
    setLocal(null);
    clear();
    closeState();
  }, [onPlayingChange, clear, closeState]);
  const onAt = useCallback(
    (ms: number) => {
      // A move that is not playback (a seek or a jump) drops what was crossed and shown.
      if (!playingRef.current) clear();
      // Several ticks can land before the flush: queue appends, never replaces.
      else if (index && atRef.current !== null) queue(crossedBetween(index, atRef.current, ms));
      atRef.current = ms;
      setLocal(ms);
    },
    [index, queue, clear],
  );
  const barState = useMemo<IReplayState>(
    () => ({
      ...state,
      close,
      // From the bar, opening is LIVE from a replayed day: that day stays on screen while today loads.
      openBar: () => {
        hold();
        openBar();
      },
      selectDate: (date: string) => {
        hold();
        setLocal(null);
        clear();
        selectStateDate(date);
      },
      // A commit in the same handler as a playing move makes it a jump (a marker click).
      commitAt: (ms: number) => {
        if (playingRef.current) clear();
        commitStateAt(ms);
      },
    }),
    [state, hold, close, openBar, clear, selectStateDate, commitStateAt],
  );
  const barProps = useMemo(
    () =>
      open
        ? {
            siteId,
            siteName,
            tz,
            state: barState,
            replay: query.data,
            loading: pending,
            held: held?.day ?? null,
            atMs: local,
            onAt,
            playing,
            onPlayingChange,
            speed,
            onSpeedChange: setSpeed,
          }
        : null,
    [open, siteId, siteName, tz, barState, query.data, pending, held, local, onAt, playing, onPlayingChange, speed],
  );
  const toggle = useCallback(() => (open ? close() : openBar()), [open, close, openBar]);
  // Only a replayed day can fail visibly; live keeps the live card.
  const failed = query.isError && at !== null;

  const shownView = loading && held ? held.view : view;

  return useMemo(
    () => ({ open, toggle, view: shownView, barProps, stamp, failed, loading }),
    [open, toggle, shownView, barProps, stamp, failed, loading],
  );
}
